import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { Store } from '@ngrx/store';
import {
  distinctUntilChanged,
  filter,
  lastValueFrom,
  Observable,
  skip,
  Subscription
} from 'rxjs';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { LoadOptions } from 'devextreme/data';
import DataSource from 'devextreme/data/data_source';
import CustomStore from 'devextreme/data/custom_store';
import { DxDataGridComponent } from 'devextreme-angular';

import { SearchAndSortValues } from 'src/app/shared/utils/list-utils';
import * as fromLocator from 'src/app/locator-store';
import { DataGridService } from '../../services/datagrid.service';
import { ChatService } from '../../services/chat.service';
import {
  deleteAiChatConversationAttempt,
  getAiChatHistoryListSucceeded,
  reloadAiChatHistoryListDataSource
} from 'src/app/reducers';
import { PeriodCategory } from '../../enums/period-category.enums';
import _ from 'lodash';
import { AiAssistantService } from '../../services/ai-assistant.service';
import { ChatHistory } from '../../models/chat-history';
import { getAiChatHistoryList, getReloadChatHistoryList } from '../../store';

@Component({
    selector: 'atlas-ai-chat-history-list',
    styleUrls: ['./ai-chat-history-list.component.less'],
    templateUrl: './ai-chat-history-list.component.html',
    standalone: false
})
export class AiChatHistoryListComponent implements OnInit {
  private subscription: Subscription = new Subscription();
  private lastLoadedData: ChatHistory[] = []; // Track last loaded data

  @ViewChild(DxDataGridComponent) dataGrid: DxDataGridComponent;

  @Input()
  template: TemplateRef<any>;

  chatHistoryList$: Observable<ChatHistory[]> =
    this.store$.select(getAiChatHistoryList);

  reloadChatHistoryList$: Observable<boolean> = this.store$.select(
    getReloadChatHistoryList
  );

  @Output() changeTab = new EventEmitter<number>();

  defaultSort: any[] = [{ selector: 'name', desc: false }];
  searchForm: UntypedFormGroup;

  customDataSource = new DataSource({
    store: new CustomStore({
      key: 'id',
      load: async (loadOptions: LoadOptions) => {
        // Default sort configuration
        if (loadOptions.sort == null) {
          loadOptions.sort = this.defaultSort;
        }

        this.removeRemoteGrouping(loadOptions);

        const result: any = await lastValueFrom(
          this.chatService.getChatHistoryList(loadOptions)
        );

        result.conversationsLoadResult.data =
          this.addTimePeriodCategoryGrouping(
            loadOptions,
            result.conversationsLoadResult.data
          );
        result.conversationsLoadResult.groupCount =
          result.conversationsLoadResult.data.length;

        // Store the loaded data for comparison
        this.lastLoadedData = [...result.conversationsLoadResult.data];

        this.store$.dispatch(
          getAiChatHistoryListSucceeded({
            chatHistoryList: result.conversationsLoadResult.data
          })
        );

        return result.conversationsLoadResult;
      }
    })
  });

  constructor(
    private fb: UntypedFormBuilder,
    private store$: Store<fromLocator.State>,
    private chatService: ChatService,
    private aiAssistantService: AiAssistantService,
    private dataGridService: DataGridService
  ) {}

  ngOnInit(): void {
    const initialValue: SearchAndSortValues = {
      filter: '',
      sort: ''
    } as SearchAndSortValues;
    this.searchForm = this.createForm(initialValue);

    // Only refresh grid when store data actually changes (not from our own dispatch)
    this.subscription.add(
      this.chatHistoryList$
        .pipe(
          skip(1), // Skip initial null/empty state
          distinctUntilChanged((prev, curr) => _.isEqual(prev, curr)),
          filter((list) => !!list)
        )
        .subscribe((list) => {
          // Only refresh if the grid is initialized and data has genuinely changed
          if (this.dataGrid?.instance && this.shouldRefreshGrid(list)) {
            this.dataGrid.instance.refresh();
          }
        })
    );

    this.subscription.add(
      this.reloadChatHistoryList$
        .pipe(filter((reload) => reload))
        .subscribe(() => {
          this.dataGrid.instance.refresh();
          this.store$.dispatch(
            reloadAiChatHistoryListDataSource({ reloadDataSource: false })
          );
        })
    );
  }

  ngAfterViewInit() {
    this.dataGridService.setDataGrid(this.dataGrid);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  createForm(initialLibraryValue: SearchAndSortValues) {
    return this.fb.group({
      filter: [initialLibraryValue.filter],
      sort: [initialLibraryValue.sort]
    });
  }

  switchToChatTab() {
    this.changeTab.emit(0);
  }

  isChatSelected(chatId: string): boolean {
    return this.aiAssistantService.isChatSelected(chatId);
  }

  openChat(event: any) {
    this.aiAssistantService.openChat(event.data.conversationId);
    this.aiAssistantService.toggleDisabledState(false);
    this.switchToChatTab();
  }

  deleteChat(event: any) {
    this.store$.dispatch(
      deleteAiChatConversationAttempt({
        conversationId: event.data.conversationId
      })
    );
  }

  private shouldRefreshGrid(newList: ChatHistory[]): boolean {
    try {
      // Compare with last loaded data instead of DataSource items
      const currentItems = this.lastLoadedData || [];

      // If lengths are different, refresh is needed
      if (currentItems.length !== newList.length) {
        return true;
      }

      // If both are empty, no refresh needed
      if (currentItems.length === 0 && newList.length === 0) {
        return false;
      }

      // Compare the actual data (excluding any UI-added properties like timePeriodCategory)
      const normalizeItem = (item: any) => ({
        id: item.id,
        conversationId: item.conversationId,
        name: item.name,
        creationDate: item.creationDate
        // Add other relevant properties that should trigger refresh
      });

      const currentNormalized = currentItems.map(normalizeItem);
      const newNormalized = newList.map(normalizeItem);

      // Use deep comparison to check if data actually changed
      return !_.isEqual(currentNormalized, newNormalized);
    } catch (error) {
      // If there's any error in comparison, err on the side of refreshing
      console.warn('Error comparing grid data, forcing refresh:', error);
      return true;
    }
  }

  private addTimePeriodCategoryGrouping(loadOptions: any, data: any[]) {
    const rowsWithCategory = data.map((item) => {
      const category = this.getTimePeriodCategory(
        item.creationDate > item.updatedDate
          ? item.creationDate
          : item.updatedDate
      );

      return { ...item, timePeriodCategory: category };
    });

    return rowsWithCategory;
  }

  private removeRemoteGrouping(loadOptions: any) {
    if (
      loadOptions?.group &&
      loadOptions?.group[0]?.selector === 'timePeriodCategory'
    ) {
      if (loadOptions?.group[0]?.selector?.includes('timePeriodCategory')) {
        loadOptions.group = [];
      }
    }

    if (
      loadOptions?.sort &&
      loadOptions?.sort.some((s: any) => s?.selector === 'timePeriodCategory')
    ) {
      loadOptions.sort = loadOptions.sort.filter(
        (s: any) => s?.selector !== 'timePeriodCategory'
      );
    }
  }

  getTimePeriodCategory(dateString: string): PeriodCategory {
    const inputDate = new Date(dateString);
    const now = new Date();

    // Normalize both dates to local time
    const inputLocalDate = new Date(
      inputDate.getFullYear(),
      inputDate.getMonth(),
      inputDate.getDate()
    );
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Check if it's today
    if (
      inputLocalDate.getFullYear() === today.getFullYear() &&
      inputLocalDate.getMonth() === today.getMonth() &&
      inputLocalDate.getDate() === today.getDate()
    ) {
      return PeriodCategory.Today;
    }

    // Get start of current week (Sunday)
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());

    // Get end of current week (Saturday)
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    if (inputLocalDate >= startOfWeek && inputLocalDate <= endOfWeek) {
      return PeriodCategory.ThisWeek;
    }

    return PeriodCategory.OverAWeek;
  }
}
